import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AssetListView } from '../components/AssetListView';
import pageThemeStyles from '../components/menuPageTheme.module.css';
import { useFavoriteAssets } from '../favorites/FavoriteAssetsContext';
import { favoriteAssetSnapshotIsComplete, favoriteListRowFromEntry } from '../favorites/favoriteAssetsStorage';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { rememberBrowseAssetListSnapshot } from '../utils/browseAssetListSnapshot';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { assetRecordApiLookupId, assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';

type FavoritesPageProps = {
  tabFocus: number;
};

export function FavoritesPage({ tabFocus }: FavoritesPageProps) {
  const { t } = useI18n();
  const { favorites, favoritesSyncing, removeFavorite } = useFavoriteAssets();
  const { showAssetInBrowse } = useAppNavigation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = 'favorites-page-title';

  const assets = useMemo(
    () =>
      favorites
        .filter((entry) => favoriteAssetSnapshotIsComplete(entry.asset))
        .map((entry) => favoriteListRowFromEntry(entry)),
    [favorites],
  );

  useEffect(() => {
    if (tabFocus < 1) {
      return;
    }
    panelRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [tabFocus]);

  const onSelect = useCallback(
    (asset: Record<string, unknown>) => {
      const row = coalesceAssetListRecord(asset);
      const id = assetRecordApiLookupId(row) ?? assetRecordPrimaryId(row);
      if (!id) {
        return;
      }
      rememberBrowseAssetListSnapshot(row);
      showAssetInBrowse(id, {
        assetTypeId: null,
        returnView: 'favorites',
        catalogBrowse: true,
      });
    },
    [showAssetInBrowse],
  );

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('favorites.title')}
    >
      <div
        ref={panelRef}
        className={[
          pageThemeStyles.panelEmbedded,
          pageThemeStyles.browsePage,
          pageThemeStyles.browsePageWide,
        ].join(' ')}
      >
        <div className={pageThemeStyles.pageHeader}>
          <span id={titleId} className={pageThemeStyles.pageTitle}>
            {t('favorites.title')}
          </span>
        </div>

        {assets.length === 0 ? (
          <p className={pageThemeStyles.browseEmpty} role="status">
            {favoritesSyncing ? t('favorites.loading') : t('favorites.empty')}
          </p>
        ) : (
          <AssetListView
            assets={assets}
            onSelect={onSelect}
            labelledBy={titleId}
            theme="light"
            layout="responsiveGrid"
            showFavoriteHeart
            showUnfavorite
            onUnfavorite={removeFavorite}
          />
        )}
      </div>
    </div>
  );
}
